/**
 * @fileoverview Shared character arc guidance constants for agent prompts.
 *
 * Single source of truth for character arc domain knowledge used across
 * creation, import, and updating agents. Each constant is a markdown
 * template string designed to be interpolated into prompt template literals.
 *
 * Parallels the per-story-type storyline density guidance in
 * `story-type-density.ts` and the narrative-element definitions in
 * `narrative-element-definitions.ts`.
 *
 * NOTE: Core arc concepts (dual-phase model, structure phases table,
 * arc progress scale) remain in `character-sub-entities-v2.ts` as they
 * are shared with schema descriptions. This file covers *quantitative*
 * guidance: beat density, summary length, and per-beat text norms.
 */

// =============================================================================
// BEAT DENSITY
// =============================================================================

/**
 * Beat count and density guidance for long-form character arcs.
 *
 * Character categories (major/minor/extra) map to density tiers:
 * major → mirrors B-storyline density, minor → mirrors C-storyline density.
 */
export const ARC_BEAT_GUIDANCE_LONG = `\
### Beat Density

The number of beats should reflect the character's importance and the depth of their transformation. Major characters need enough beats to show a nuanced, multi-phase journey; minor characters need fewer but should still mark the key inflection points.

| Category | Total beats | Per-phase target | Max per phase |
|----------|------------|-----------------|---------------|
| **Major** | 10-14 | 1.5-2 beats per phase | 4 |
| **Minor** | 5-8 | ~1 beat per phase (spanning fewer phases) | 2 |

**Multiple beats per phase:** Use the \`order\` field (0, 1, 2, ...) to sequence multiple beats within the same globalPhase. Create multiple beats when psychological shifts are **distinct enough to warrant separate tracking** — don't merge unrelated internal shifts into a single beat, and don't artificially split a single realization into multiple beats.

**Phase coverage:**
- **Major characters**: Cover all 7 structural phases. Multiple beats per phase capture the richness of a character's internal journey across longer fiction.
- **Minor characters**: Cover Initial State, Final State, and the phases where the character's arc turns most sharply. Skip phases where the character is offstage or static.`;

/**
 * Beat count and density guidance for short-form character arcs.
 */
export const ARC_BEAT_GUIDANCE_SHORT = `\
### Beat Density (Short-Form)

In short fiction, character arcs are captured entirely through the arc summary — **do not produce beats**. Output an empty \`beats\` array \`[]\` for every character.

The arc summary carries all the detail. See the summary guidance for appropriate length by character category.`;

// =============================================================================
// SUMMARY & NAMING
// =============================================================================

/**
 * Arc summary length guidance, differentiated by form and whether beats
 * are present.
 */
export const ARC_SUMMARY_GUIDANCE = `\
### Arc Summary

The summary provides a high-level overview of the character's transformation. Its length depends on context:

| Context | Summary length | Rationale |
|---------|---------------|-----------|
| **Long-form** (with beats) | 1-3 sentences | Beats carry the phase-by-phase detail; the summary is a quick orientation. |
| **Short-form, major character** | 4-6 sentences | No beats — the summary IS the arc. Must capture starting state, key turning points, and resolution with enough specificity to guide writing. |
| **Short-form, minor character** | 1-3 sentences | Brief sketch of whatever change the character undergoes. |

The summary should always name the character's core internal conflict (want vs. need, lie vs. truth) and describe the direction of change.`;

/**
 * Arc naming guidance.
 */
export const ARC_NAMING = `\
### Arc Naming

Use a short, evocative title (3-6 words) that captures the *direction* of transformation, not just a theme:
- Positive arc: "From Coward to Hero", "Learning to Trust Again"
- Negative arc: "The Slow Corruption", "Losing What Matters"
- Flat arc: "The Unyielding Conscience", "Steadfast Against the Tide"

Avoid generic labels like "Character Development" or "The Journey".`;

// =============================================================================
// PER-BEAT TEXT NORMS
// =============================================================================

/**
 * Guidance on the content and length of individual beat fields.
 * Used by creation, extraction, and refresh agents.
 */
export const BEAT_TEXT_NORMS = `\
### Beat Text Guidelines

Each beat captures a single psychological moment. Keep the text focused:

- **internalState** (required): 1-3 sentences describing the character's emotional realization, decision, or psychological shift at this phase. Be specific — reference the concrete story event that triggers the shift, then describe what changes inside the character.
- **behavior** (optional but recommended): 1 sentence describing the observable action that results from this internal shift. If the shift has no visible behavioral consequence yet, omit rather than force it.
- **arcProgress**: Position on the truth/lie spectrum (-1 to +1). Ensure values form a coherent trajectory across beats — avoid random jumps unless a sudden reversal is intentional and story-justified.

**Tone:** Write in present tense. Focus on the *internal* journey (~80%) with observable behavior (~20%). Be concrete, not abstract — "realizes her mentor has been lying" rather than "gains new understanding".`;
